import { useEffect, useState } from "react";
import Modal from "./Modal";
import {
	WEEKLY_DAYS_MON_SAT,
	emptyWeeklyDayMap,
	normalizeTimeValue,
	parseWeeklySchedule,
	serializeWeeklySchedule,
} from "../../utils/weeklySchedule";
import "./WeeklyScheduleModal.css";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} [props.initialSchedule]
 * @param {(schedule: string) => void} props.onApply
 */
export default function WeeklyScheduleModal({
	open,
	onClose,
	initialSchedule = "",
	onApply,
}) {
	const [dayMap, setDayMap] = useState(() => emptyWeeklyDayMap());

	useEffect(() => {
		if (!open) return;
		const parsed = parseWeeklySchedule(initialSchedule);
		if (parsed) setDayMap({ ...parsed });
		else setDayMap(emptyWeeklyDayMap());
	}, [open, initialSchedule]);

	const legacyText =
		(initialSchedule ?? "").trim() &&
		!parseWeeklySchedule(initialSchedule)
			? String(initialSchedule).trim()
			: "";

	function setDayTime(dayId, type, value) {
		setDayMap((prev) => {
			const current = prev[dayId] || { start: "", end: "" };
			return {
				...prev,
				[dayId]: {
					...current,
					[type]: value,
				},
			};
		});
	}

	function handleTimeBlur(dayId, type, raw) {
		const n = normalizeTimeValue(raw);
		if (n !== raw) setDayTime(dayId, type, n);
	}

	function handleApply() {
		const s = serializeWeeklySchedule(dayMap);
		onApply(s);
		onClose();
	}

	function handleTimeKeyDown(e) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.currentTarget.blur();
		}
	}

	return (
		<Modal
			title="Horario semanal (Lun – Sáb)"
			open={open}
			onClose={onClose}
			nested
			dialogClassName="modal-dialog modal-dialog-centered modal-lg weekly-schedule-modal"
			footer={
				<>
					<button
						type="button"
						className="adm-del-btn"
						onClick={onClose}
					>
						<span>Cancelar</span>
					</button>
					<button
						type="button"
						className="adm-save-btn"
						onClick={handleApply}
					>
						<span>Aplicar horario</span>
					</button>
				</>
			}
		>
			<div className="font-n">
			<p className=" mb-3">
				Elige un día (Lunes a Sábado) y la hora de clase. Los días sin
				hora quedan sin clase. Pulsa el siguiente campo para continuar.
			</p>
			{legacyText ? (
				<div className="alert alert-light border small mb-3 py-2">
					<span><strong className="d-block mb-1">Horario guardado (texto)</strong></span>
					<span className="text-break">{legacyText}</span>
					<span className="d-block mt-1 text-muted">
						Al aplicar el nuevo horario semanal, reemplazará este
						texto.
					</span>
				</div>
			) : null}

			<div className="weekly-schedule-strip">
				{WEEKLY_DAYS_MON_SAT.map((d) => {
						const times = dayMap[d.id] || { start: "", end: "" };
						const active = Boolean(times.start || times.end);
						return (
							<div
								key={d.id}
								className={`weekly-schedule-day-cell${active ? " weekly-schedule-day-cell--active" : ""}`}
							>
								<div className="weekly-schedule-day-short mb-2">
									<span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{d.short}</span>
								</div>
								<div className="d-flex flex-column gap-2">
									<div>
										<div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '2px', lineHeight: 1 }}>Inicio</div>
										<input
											type="time"
											className="form-control form-control-sm weekly-schedule-time-input"
											value={times.start}
											placeholder="Selecciona una hora" onChange={(e) => setDayTime(d.id, "start", e.target.value)}
											onBlur={(e) => handleTimeBlur(d.id, "start", e.target.value)}
											onKeyDown={handleTimeKeyDown}
											min="08:00"
											max="22:00"
											title="Hora de inicio"
										/>
									</div>
									<div>
										<div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '2px', lineHeight: 1 }}>Final</div>
										<input
											type="time"
											className="form-control form-control-sm weekly-schedule-time-input"
											value={times.end}
											placeholder="Selecciona una hora" onChange={(e) => setDayTime(d.id, "end", e.target.value)}
											onBlur={(e) => handleTimeBlur(d.id, "end", e.target.value)}
											onKeyDown={handleTimeKeyDown}
											min="08:00"
											max="22:00"
											title="Hora de término"
										/>
									</div>
								</div>
							</div>
						);
					})}
			</div>

			<div className="weekly-schedule-list">
				{WEEKLY_DAYS_MON_SAT.map((d) => {
						const times = dayMap[d.id] || { start: "", end: "" };
						return (
							<div key={d.id} className="weekly-schedule-row mb-2 d-flex align-items-center">
								<label style={{ width: "90px", fontWeight: "600" }} htmlFor={`ws-time-${d.id}-start`}>
									{d.label}
								</label>
								<div className="d-flex align-items-center gap-2">
									<input
										id={`ws-time-${d.id}-start`}
										type="time"
										className="form-control form-control-sm"
										style={{ maxWidth: 120 }}
										value={times.start} onChange={(e) => setDayTime(d.id, "start", e.target.value)}
										onBlur={(e) => handleTimeBlur(d.id, "start", e.target.value)}
										onKeyDown={handleTimeKeyDown}
										min="08:00"
										max="22:00"
										placeholder="Inicio"
										title="Hora de inicio (08:00 - 22:00)"
									/>
									<span className="text-muted">a</span>
									<input
										id={`ws-time-${d.id}-end`}
										type="time"
										className="form-control form-control-sm"
										style={{ maxWidth: 120 }}
										value={times.end} onChange={(e) => setDayTime(d.id, "end", e.target.value)}
										onBlur={(e) => handleTimeBlur(d.id, "end", e.target.value)}
										onKeyDown={handleTimeKeyDown}
										min="08:00"
										max="22:00"
										placeholder="Fin"
										title="Hora de término (08:00 - 22:00)"
									/>
								</div>
							</div>
						);
					})}
			</div>
			</div>
		</Modal>
	);
}
